import React from "react";
import ErrorBoundary from "../../../ErrorBoundary";
import { StepsStepper } from "./StepsStepper";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { handleSubmitWebhooks } from "./handleSubmitWebhooks";
import {
  SiteWebhookFormFields,
  SiteWebhookFormValues,
} from "../../common/SiteWebhookFormFields";
import {
  EMPTY_WEBHOOK_AUTH_VALUES,
  WebhookFieldErrors,
  authBodyJsonError,
  buildWebhookAuthCreatePayload,
} from "../../utils/webhookAuth";
import { jsonTextError } from "../Sites/SiteAboutPage/SiteAboutPageUtils/siteWebhooks";

declare module "@mui/material/styles" {
  interface Palette {
    neutral: Palette["primary"];
  }
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#FF8400",
    },
  },
});

export interface AddWebhookFormValues extends SiteWebhookFormValues {
  customer: number;
}

/**
 * Onboarding step shown when the new site's Default Alarm is Webhooks. The
 * fields are shared with Site About's "+ Add Webhook" dialog (ENG-2233), so
 * both post the same shape to `webhook_customer/`.
 */
export const AddWebhookScreen = ({
  setVisibleScreen,
  siteId,
  initialValues,
  fieldErrors = {},
}: {
  setVisibleScreen: any;
  siteId: number;
  // Set when re-rendered after a rejected submit, so the input isn't lost.
  initialValues?: AddWebhookFormValues;
  fieldErrors?: WebhookFieldErrors;
}) => {
  const [webhooksInfo, setWebhooksInfo] = React.useState<AddWebhookFormValues>(
    initialValues ?? {
      customer: siteId,
      display_name: "",
      target_url: "",
      method: "POST",
      message_body: "",
      auth: EMPTY_WEBHOOK_AUTH_VALUES,
    }
  );

  const navigate = useNavigate();

  return (
    <ErrorBoundary>
      <div>
        <div className="absolute top-20 right-[15px]">
          <div className="flex justify-end">
            <StepsStepper activeStep={1} />
          </div>
        </div>
        <p
          className={
            "font-mulish absolute top-[130px] text-[#283E6D] right-[130px] text-[18px]"
          }
        >
          Please complete the following Webhooks information:
        </p>
        <div className="absolute right-[215px] top-[180px]">
          <SiteWebhookFormFields
            values={webhooksInfo}
            fieldErrors={fieldErrors}
            onFieldChange={(field, value) => {
              setWebhooksInfo((previousState) => ({
                ...previousState,
                [field]: value,
              }));
            }}
            onAuthFieldChange={(field, value) => {
              setWebhooksInfo((previousState) => ({
                ...previousState,
                auth: { ...previousState.auth, [field]: value },
              }));
            }}
          />

          <div className="relative left-[400px] pt-[20px]">
            <ThemeProvider theme={theme}>
              <Button
                className="shadow-button bg-actuate-orange rounded font-mulish text-white text-base p-1.5"
                type="submit"
                disabled={
                  !webhooksInfo.display_name ||
                  !webhooksInfo.target_url ||
                  Boolean(authBodyJsonError(webhooksInfo.auth.auth_body)) ||
                  Boolean(
                    jsonTextError(webhooksInfo.message_body, "Message Body")
                  )
                }
                onClick={() => {
                  const { auth, ...rest } = webhooksInfo;
                  handleSubmitWebhooks(
                    { ...rest, ...buildWebhookAuthCreatePayload(auth) },
                    setVisibleScreen,
                    navigate,
                    webhooksInfo
                  );
                }}
              >
                Submit
              </Button>
            </ThemeProvider>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};
